import { useEffect, useState, type FormEvent } from 'react';

import type { CustomerDetail } from '@domain/customer/Customer';
import {
  InvalidAdjustment,
  adjustBalance,
} from '@application/customer/adjustBalance';
import { apiClient } from '@infrastructure/api/client';
import { errorText } from '@infrastructure/api/errors';
import { HttpCustomerRepository } from '@infrastructure/api/HttpCustomerRepository';

import { Modal } from './Modal';

const repo = new HttpCustomerRepository(apiClient);

interface Props {
  open: boolean;
  customer: CustomerDetail | null;
  onClose: () => void;
  onSaved: (updated: CustomerDetail) => void;
}

export function BalanceAdjustModal({ open, customer, onClose, onSaved }: Props) {
  const [direction, setDirection] = useState<'credit' | 'debit'>('credit');
  const [magnitude, setMagnitude] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One key per opening of the modal: a retry after a timeout or error reuses
  // it, so the server applies the adjustment at most once.
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());

  useEffect(() => {
    if (open) setIdempotencyKey(crypto.randomUUID());
  }, [open]);

  if (!customer) return null;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const amount = direction === 'credit' ? magnitude : `-${magnitude}`;
      const updated = await adjustBalance(repo)(customer, { amount, reason, idempotencyKey });
      onSaved(updated);
      onClose();
      setMagnitude('');
      setReason('');
    } catch (e) {
      setError(
        e instanceof InvalidAdjustment ? e.message : errorText(e),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} title={`Adjust balance — ${customer.company_name}`} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-3 text-sm">
        <div className="rounded bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Current balance: <strong>${customer.balance}</strong>
        </div>
        <label className="block">
          Direction
          <select
            value={direction}
            onChange={(e) => { setDirection(e.target.value as 'credit' | 'debit'); setIdempotencyKey(crypto.randomUUID()); }}
            className="mt-1 w-full rounded border px-2 py-1"
          >
            <option value="credit">Credit (+ add funds)</option>
            <option value="debit">Debit (− remove funds)</option>
          </select>
        </label>
        <label className="block">
          Amount ($)
          <input
            type="number" step="0.01" min="0.01" required
            value={magnitude}
            onChange={(e) => { setMagnitude(e.target.value); setIdempotencyKey(crypto.randomUUID()); }}
            className="mt-1 w-full rounded border px-2 py-1"
          />
        </label>
        <label className="block">
          Reason (required for audit log)
          <textarea
            required minLength={3} rows={3}
            value={reason} onChange={(e) => setReason(e.target.value)}
            className="mt-1 w-full rounded border px-2 py-1"
          />
        </label>
        {error && <p className="text-red-700">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded border px-3 py-1">
            Cancel
          </button>
          <button
            type="submit" disabled={submitting}
            className="rounded bg-blue-600 px-3 py-1 text-white disabled:opacity-50"
          >
            {submitting ? 'Applying…' : 'Apply adjustment'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
